import { useMemo, useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { LESSONS } from "@/content/lessons";
import { CATEGORIES, AGE_BANDS, DIFFICULTIES } from "@/content/categories";
import LessonCard from "@/components/education/LessonCard";
import PageHero from "@/components/shared/PageHero";
import Seo from "@/components/shared/Seo";
import Icon from "@/components/shared/Icon";

export default function Learn() {
  const { t, code } = useLang();
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState(params.get("cat") || "all");
  const [age, setAge] = useState("all");
  const [diff, setDiff] = useState("all");
  const [sort, setSort] = useState("recommended");

  useEffect(() => {
    const c = params.get("cat");
    if (c) setCat(c);
  }, [params]);

  const results = useMemo(() => {
    let list = LESSONS.filter((l) => {
      if (cat !== "all" && l.category !== cat) return false;
      if (age !== "all" && l.age !== age) return false;
      if (diff !== "all" && l.difficulty !== diff) return false;
      if (q.trim()) {
        const text = `${l.title.en} ${l.title.zh} ${l.desc.en} ${l.desc.zh}`.toLowerCase();
        if (!text.includes(q.trim().toLowerCase())) return false;
      }
      return true;
    });
    if (sort === "shortest") list = [...list].sort((a, b) => a.minutes - b.minutes);
    if (sort === "az") list = [...list].sort((a, b) => a.title[code].localeCompare(b.title[code]));
    return list;
  }, [q, cat, age, diff, sort, code]);

  const selectCls =
    "rounded-2xl border-2 border-[#147DEB]/15 bg-white px-4 py-2.5 text-sm font-semibold text-[#082B66] focus:border-[#147DEB] outline-none";

  return (
    <>
      <Seo title={t("learn.title")} description={t("learn.subtitle")} />
      <PageHero eyebrow={t("nav.learn")} title={t("learn.title")} subtitle={t("learn.subtitle")} />

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Controls */}
        <div className="rounded-3xl border-2 border-[#147DEB]/10 bg-white p-5 card-shadow-soft">
          <div className="relative">
            <Icon name="Search" className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#94A3B8]" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("common.searchPlaceholder")}
              data-testid="lesson-search-input"
              aria-label={t("common.search")}
              className="w-full rounded-2xl border-2 border-[#147DEB]/15 bg-[#F8FBFF] py-3 pl-12 pr-4 text-base text-[#082B66] outline-none focus:border-[#147DEB]"
            />
          </div>

          <div className="mt-4 flex flex-wrap gap-3">
            <select value={cat} onChange={(e) => { setCat(e.target.value); setParams(e.target.value === "all" ? {} : { cat: e.target.value }); }} className={selectCls} data-testid="filter-category" aria-label={t("common.category")}>
              <option value="all">{t("common.all")} · {t("common.category")}</option>
              {CATEGORIES.map((c) => (
                <option key={c.id} value={c.id}>{c.name[code]}</option>
              ))}
            </select>
            <select value={age} onChange={(e) => setAge(e.target.value)} className={selectCls} data-testid="filter-age" aria-label={t("common.age")}>
              <option value="all">{t("common.all")} · {t("common.age")}</option>
              {AGE_BANDS.map((a) => (
                <option key={a} value={a}>{t("common.age")} {a}</option>
              ))}
            </select>
            <select value={diff} onChange={(e) => setDiff(e.target.value)} className={selectCls} data-testid="filter-difficulty" aria-label={t("common.difficulty")}>
              <option value="all">{t("common.all")} · {t("common.difficulty")}</option>
              {DIFFICULTIES.map((d) => (
                <option key={d.id} value={d.id}>{t(d.key)}</option>
              ))}
            </select>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className={selectCls} data-testid="filter-sort" aria-label={t("common.sortBy")}>
              <option value="recommended">{t("learn.sort.recommended")}</option>
              <option value="shortest">{t("learn.sort.shortest")}</option>
              <option value="az">{t("learn.sort.az")}</option>
            </select>
          </div>
        </div>

        <p className="mt-6 text-sm font-semibold text-[#64748B]" data-testid="lessons-count">{t("learn.lessonsCount", { n: results.length })}</p>

        {results.length === 0 ? (
          <div className="mt-6 rounded-3xl border-2 border-dashed border-[#147DEB]/20 bg-white p-12 text-center" data-testid="lessons-empty">
            <Icon name="SearchX" className="mx-auto h-10 w-10 text-[#94A3B8]" />
            <p className="mt-3 text-base font-semibold text-[#475569]">{t("common.noResults")}</p>
          </div>
        ) : (
          <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {results.map((l) => (
              <LessonCard key={l.slug} lesson={l} />
            ))}
          </div>
        )}
      </section>
    </>
  );
}
